import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const Highlights = () => {
  const sectionRef = useRef();
  const lineRef = useRef();
  const labelRef = useRef();
  const headingRef = useRef();
  const highlightItemsRef = useRef([]);
  const lettersRef = useRef([]);

  useEffect(() => {
    // Animation de la ligne
    gsap.fromTo(lineRef.current,
      { scaleX: 0, opacity: 0 },
      {
        scaleX: 1,
        opacity: 1,
        duration: 1.5,
        ease: "power3.out",
        scrollTrigger: {
          trigger: lineRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation du label
    gsap.fromTo(labelRef.current,
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        delay: 0.3,
        ease: "power2.out",
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation du texte split
    const headingWords = headingRef.current.querySelectorAll('.word');
    gsap.fromTo(headingWords,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation des items highlights
    const highlightItems = highlightItemsRef.current;
    highlightItems.forEach((item, index) => {
      if (item) {
        gsap.fromTo(item,
          { 
            y: 60, 
            opacity: 0,
            scale: 0.95
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 1,
            delay: index * 0.15,
            ease: "power3.out",
            scrollTrigger: {
              trigger: item,
              start: "top 90%",
              toggleActions: "play none none reverse"
            }
          }
        );

        // Hover effect sur les cartes
        item.addEventListener('mouseenter', () => {
          gsap.to(item, {
            y: -8,
            duration: 0.4,
            ease: "power2.out"
          });
        });

        item.addEventListener('mouseleave', () => {
          gsap.to(item, {
            y: 0,
            duration: 0.4,
            ease: "power2.out"
          });
        });
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addToHighlightRef = (el) => {
    if (el && !highlightItemsRef.current.includes(el)) {
      highlightItemsRef.current.push(el);
    }
  };

  // Split text en mots pour l'animation
  const headingText = "From international stages to creative collaborations, these milestones represent the journey of artistic growth and cultural impact.";
  const words = headingText.split(' ');

  // Données des highlights
  const highlights = [
    {
      id: 1,
      title: "SPAIN'S GOT TALENT",
      image: "/assets/6.jpg",
      alt: "Safaa Shiey performing on Spain's Got Talent stage",
      description: "Reaching the semifinals of Spain's Got Talent was a significant milestone for Safaa Shiey. Her participation in this television program marked a standout moment in her career, opening doors internationally and affirming her potential as an emerging music artist.",
      features: [
        "Reached the semifinals of Spain's Got Talent, gaining national and international recognition",
        "Performed with passion and technical precision, impressing both judges and audiences",
        "Expanded her artistic presence across multiple countries, including the UK, Lebanon, Turkey, France, and Italy",
        "Demonstrated her ability to excel in high-level competitions, confirming her potential as an emerging artist"
      ],
      badge: "Music"
    },
    {
      id: 2,
      title: "CANNES FILM FESTIVAL",
      image: "/assets/21.jpg",
      alt: "Safaa Shiey at Cannes Film Festival red carpet",
      description: "Recently recognized for her outstanding talent, she had the honor of performing at the prestigious Nelson Mandela Gala during the Cannes Film Festival (May 2024).",
      features: [
        "Performed at the exclusive Nelson Mandela Gala at the Cannes Film Festival",
        "Gained significant international exposure and recognition in the music industry",
        "Demonstrated exceptional vocal performance on a highly visible and prestigious platform",
        "Achieved a pivotal career milestone, showcasing her talent to a global audience"
      ],
      badge: "Cinema"
    },
    {
      id: 3,
      title: "MOROCCAN MUSIC INDUSTRY",
      image: "/assets/24.jpg",
      alt: "Safaa Shiey in Moroccan music studio collaboration",
      description: "In addition to her international success, Safaa is committed to establishing her presence in Morocco’s music industry, blending her soulful style with the richness of her cultural roots.",
      features: [
        "Aims to expand her artistic journey within the Moroccan music scene",
        "Combines international experience with a deep passion for soul music",
        "Showcases versatility through collaborations and new creative projects",
        "Seeks to bridge global influences with Morocco’s evolving musical identity"
      ],
      badge: "Culture"
    },
    {
      id: 4,
      title: "WORK IN PROGRESS",
      image: "/assets/2.jpg",
      alt: "Safaa Shiey in creative studio session",
      description: "“Un poco de mucho” is a podcast that explores a wide range of life experiences through honest and engaging conversations with diverse guests.",
      features: [
        "Co-conceptualized and developed the podcast with Olivia Guillén",
        "Created a space for open discussions about everyday human experiences",
        "Produced and hosted episodes featuring guests from various backgrounds",
        "Highlighted personal stories to inspire reflection and connection among listeners"
      ],
      badge: "Coming Soon"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="section_highlights relative bg-gray-50 py-20 lg:py-32 overflow-hidden"
    >
      {/* Espacement initial */}
      <div className="padding-section-medium h-16 lg:h-24" id="highlights"/>

      {/* Ligne décorative */}
      <div className="px-6 lg:px-8">
        <div 
          ref={lineRef}
          className="line w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent max-w-7xl mx-auto"
        />
      </div>

      <div className="padding-section-medium h-16 lg:h-24" />

      {/* Contenu principal */}
      <div className="px-6 lg:px-8">
        <div className="container-medium max-w-7xl mx-auto">
          
          {/* En-tête */}
          <div className="highlights_header grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 lg:mb-24">
            
            {/* Label */}
            <div className="lg:col-span-4">
              <div 
                ref={labelRef}
                className="label_wrap flex items-center space-x-4"
              >
                <div className="label_line w-12 h-px bg-gradient-to-r from-blue-500 to-purple-500" />
                <h2 className="text-style-label text-sm font-bold text-gray-700 tracking-widest uppercase">
                  Highlights
                </h2>
              </div>
            </div>

            {/* Heading avec animation split */}
            <div className="lg:col-span-8">
              <h2 
                ref={headingRef}
                className="heading-style-h3 text-3xl lg:text-4xl xl:text-5xl font-light text-gray-600 leading-tight"
              >
                {words.map((word, index) => (
                  <span key={index} className="word inline-block mr-1">
                    {word}
                  </span>
                ))}
              </h2>
            </div>

          </div>

          {/* Grid des highlights */}
          <div className="highlights_grid grid grid-cols-1 lg:grid-cols-2 gap-8">
            {highlights.map((highlight) => (
              <div
                key={highlight.id}
                ref={addToHighlightRef}
                className="highlight_item group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-100"
              >
                <div className="highlight_img-wrap relative overflow-hidden">
                  <img
                    src={highlight.image}
                    alt={highlight.alt}
                    loading="lazy"
                    className="highlight_img w-full h-48 object-cover object-[50%_20%] transition-transform duration-700 group-hover:scale-110"
                  />
                  
                  {/* Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="bg-black/80 text-white px-3 py-1 rounded-full text-xs font-medium tracking-wide backdrop-blur-sm">
                      {highlight.badge}
                    </span>
                  </div>

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>

                <div className="highlight_content p-6 lg:p-8">
                  <h3 className="heading-style-h4 text-2xl lg:text-3xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    {highlight.title}
                  </h3>
                  
                  <p className="highlight_description text-gray-600 leading-relaxed mb-6">
                    {highlight.description}
                  </p>

                  {/* Liste des features */}
                  <ul className="space-y-3">
                    {highlight.features.map((feature, index) => (
                      <li 
                        key={index}
                        className="flex items-start space-x-3 text-gray-700 leading-relaxed"
                      >
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          highlight.id === 1 ? 'bg-red-500' :
                          highlight.id === 2 ? 'bg-yellow-500' :
                          highlight.id === 3 ? 'bg-green-500' : 'bg-purple-500'
                        }`} />
                        <span className="text-sm lg:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Ligne décorative en bas */}
                  <div className={`mt-6 w-12 h-0.5 rounded-full ${
                    highlight.id === 1 ? 'bg-red-500' :
                    highlight.id === 2 ? 'bg-yellow-500' :
                    highlight.id === 3 ? 'bg-green-500' : 'bg-purple-500'
                  }`} />
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Espacement final */}
      <div className="padding-section-xsmall h-12 lg:h-16" />

      <style jsx>{`
        .section_highlights {
          perspective: 1000px;
        }

        .highlight_img {
          transform-origin: center;
        }

        .word {
          transform-origin: center bottom;
          backface-visibility: hidden;
        }

        .highlight_item {
          transform-style: preserve-3d;
          transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
        }

        .highlight_item:hover {
          transform: translateY(-4px);
        }

        @media (max-width: 1024px) {
          .highlights_header {
            gap: 6;
          }
          
          .highlights_grid {
            gap: 6;
          }
        }

        @media (max-width: 768px) {
          .heading-style-h3 {
            font-size: 2rem;
          }
          
          .highlight_content {
            padding: 1.5rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Highlights;